/**
 * Set Powers Configuration
 *
 * This file defines the tutorial configurations for different emoji chess sets.
 * Each set can have custom tutorial behavior including auto-start functionality.
 *
 * Example usage of autoStart feature:
 *
 * ```typescript
 * tutorialConfig: {
 *   autoStart: true,  // Tutorial starts immediately when component loads
 *   counterAcceleration: { ... }
 * }
 * ```
 *
 * Example usage of startOnBoardClick feature:
 *
 * ```typescript
 * tutorialConfig: {
 *   startOnBoardClick: true,  // Tutorial starts when user clicks anywhere on the board
 *   counterAcceleration: { ... }
 * }
 * ```
 *
 * Example usage of custom FEN positions:
 *
 * ```typescript
 * tutorialSteps: [
 *   {
 *     id: "custom-position",
 *     type: "click",
 *     title: "Custom Board Position",
 *     description: "This step uses a custom board position",
 *     fen: "rnbqkb1r/pppp1ppp/5n2/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq - 2 3",
 *     // ... other step properties
 *   }
 * ]
 * ```
 *
 * When autoStart is true: Tutorial begins automatically without user interaction
 * When autoStart is false/undefined: User must click "Try the Power!" button to start
 * When startOnBoardClick is true: Clicking anywhere on the board will start the tutorial
 * When startOnBoardClick is false/undefined: Board clicks won't start the tutorial
 * When fen is provided: The board will be set to that position when the step begins
 * When fen is undefined: The board position remains unchanged from the previous step
 */

export interface TutorialStep {
  id: string;
  type:
    | "click"
    | "move"
    | "wait"
    | "observe"
    | "aiPlay"
    | "turnChange"
    | "playerMove";
  title: string;
  description: string;
  tooltip?: string;
  targetSquare?: string;
  fromSquare?: string;
  toSquare?: string;
  expectedPiece?: string;
  highlightSquares?: string[];
  // Valid moves in chessground format: {"a2": ["a3", "a4"], "b1": ["a3", "c3"]}
  validMoves?: Record<string, string[]>;
  // Custom FEN position for this step (optional)
  fen?: string;
  animation?: {
    type: "pulse" | "glow" | "bounce" | "flash" | "arrow";
    duration?: number;
    color?: string;
    direction?: "up" | "down" | "left" | "right" | "diagonal";
  };
  delay?: number;
  validation?: {
    powerTriggered?: string;
    counterChanged?: string;
    pieceAdded?: boolean;
    timeDecreased?: boolean;
  };
  autoAdvance?: {
    enabled: boolean;
    delay?: number;
  };
  errorHandling?: {
    wrongSquareMessage: string;
    hintMessage: string;
  };
  // AI play configuration
  aiPlay?: {
    delay?: number;
    firstMove?: boolean;
  };
}

export interface TutorialConfig {
  // Auto-start tutorial without requiring user to click "Try the Power!" button
  // When set to true, the tutorial will begin immediately when the component loads
  // When set to false or undefined, users must click the "Try the Power!" button to start
  autoStart?: boolean;
  // Start tutorial when the board is clicked (if tutorial is not active and at step 0)
  // When set to true, clicking anywhere on the board will start the tutorial
  // When set to false or undefined, board clicks won't start the tutorial
  startOnBoardClick?: boolean;
  // Counter acceleration for tutorial mode
  counterAcceleration?: {
    counterKey: string;
    multiplier: number;
    maxValue: number;
    autoAdvanceStepId?: string; // Step to auto-advance when max value reached
  };
  // Custom tutorial logic handlers
  customHandlers?: {
    onCounterUpdate?: (
      counterKey: string,
      newValue: unknown,
      oldValue: unknown
    ) => void;
    onStepAdvance?: (fromStepId: string, toStepId: string) => void;
  };
}

export interface SetPowerInfo {
  name: string;
  description: string;
  keyPower: string;
  animationDescription: string;
  demonstrationMoves: Array<{
    from: string;
    to: string;
    piece: string;
    description: string;
  }>;
  tutorialSteps: TutorialStep[];
  constants?: Record<string, number>;
  tutorialConfig?: TutorialConfig;
}

export const SET_POWERS: Record<string, SetPowerInfo> = {
  dogs: {
    name: "Dogs",
    description: "Loyal companions who love to dig and bury bones",
    keyPower: "Bone Digging",
    animationDescription:
      "Click empty squares to dig, summon pawns when fully dug. You lose 2 seconds per click when you dig",
    demonstrationMoves: [
      {
        from: "e4",
        to: "e4",
        piece: "🦴",
        description: "Click empty squares to dig and accumulate bones",
      },
      {
        from: "e4",
        to: "e4",
        piece: "P",
        description: "Summon pawn when enough bones gathered",
      },
    ],
    constants: {
      BONE_THRESHOLD: 40,
      MAX_SUMMONS: 2,
      TIME_PENALTY: 2000, // 2 seconds in milliseconds
    },
    tutorialConfig: {
      autoStart: false,
      startOnBoardClick: true,
      counterAcceleration: {
        counterKey: "dog-bone-increment",
        multiplier: 4,
        maxValue: 40,
      },
    },
    tutorialSteps: [
      {
        id: "intro",
        type: "observe",
        title: "Welcome to Dog Power!",
        description:
          "Dogs can dig for bones and summon pawns. Let's learn how!",
        tooltip: "Watch as we demonstrate the bone digging mechanic",
        autoAdvance: {
          enabled: true,
          delay: 3000,
        },
      },
      {
        id: "first-dig",
        type: "click",
        title: "Start Digging",
        description: "Click on an empty square to start digging for bones",
        tooltip: "Click here to dig! Each click costs 2 seconds of your time.",
        highlightSquares: ["e4"],
        animation: {
          type: "pulse",
          duration: 1500,
          color: "#fbbf24", // amber
        },
        validation: {
          powerTriggered: "dog-bone-increment",
          timeDecreased: true,
        },

        errorHandling: {
          wrongSquareMessage: "Try clicking on the highlighted square (e4)",
          hintMessage:
            "Remember: you can only dig on empty squares (not on the edges)",
        },
      },
      {
        id: "continue-digging",
        type: "click",
        title: "Keep Digging",
        description: "Keep clicking the same square to accumulate more bones",
        tooltip:
          "Click the same square multiple times to dig deeper. You need 40 clicks total!",
        targetSquare: "e4",
        highlightSquares: ["e4"],
        animation: {
          type: "bounce",
          duration: 1000,
          color: "#f59e0b", // amber-500
        },
        validation: {
          powerTriggered: "dog-bone-increment",
          counterChanged: "dog-bone-summon",
          pieceAdded: true,
        },
        errorHandling: {
          wrongSquareMessage:
            "Keep clicking the same square (e4) to dig up the bone",
          hintMessage:
            "Each click adds bones but costs time. Watch your counter!",
        },
      },
      {
        id: "completion",
        type: "observe",
        title: "Well Done!",
        description:
          "You've successfully used the Dog power! You can summon up to 2 pawns per game.",
        tooltip:
          "Great job! Remember: digging costs time, but gives you extra pieces.",
        highlightSquares: ["e4"],
        targetSquare: "e4",
        animation: {
          type: "glow",
          duration: 20000,
          color: "#10b981", // emerald-500 for success
        },
        autoAdvance: {
          enabled: true,
          delay: 0,
        },
      },
    ],
  },
  mammals: {
    name: "Mammals",
    description: "Agile creatures with enhanced pawn mobility",
    keyPower: "Diagonal Leap",
    animationDescription:
      "Twice per game, your pawns can move diagonally forward one empty square (without capturing)",
    demonstrationMoves: [
      {
        from: "e2",
        to: "d3",
        piece: "P",
        description: "Pawn moves diagonally forward to empty square",
      },
      {
        from: "d3",
        to: "e4",
        piece: "P",
        description: "Another diagonal leap to an empty square",
      },
    ],
    constants: {
      MAX_USES: 2,
    },
    tutorialConfig: {
      autoStart: false,
      startOnBoardClick: true,
    },
    tutorialSteps: [
      {
        id: "intro",
        type: "observe",
        title: "Welcome to Mammal Power!",
        description:
          "Mammals have agile pawns that can leap diagonally to empty squares. Let's learn how!",
        tooltip: "Watch as we demonstrate the diagonal leap mechanic",
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
      {
        id: "select-pawn",
        type: "click",
        title: "Select Your Pawn",
        description: "First, click on one of your pawns to select it",
        tooltip: "Click on this pawn to select it for a diagonal leap",
        targetSquare: "e2",
        highlightSquares: ["e2"],
        expectedPiece: "p",

        animation: {
          type: "pulse",
          duration: 1500,
          color: "#3b82f6", // blue
        },
        errorHandling: {
          wrongSquareMessage: "Try clicking on the highlighted pawn (e2)",
          hintMessage: "You need to select a pawn first before moving it",
        },
      },
      {
        id: "diagonal-move",
        type: "click",
        title: "Make Diagonal Leap",
        description: "Now click on an empty diagonal square to leap there",
        tooltip:
          "Click on this empty diagonal square. Pawns can leap diagonally forward without capturing!",
        targetSquare: "d3",
        highlightSquares: ["d3", "f3"],
        // Allow diagonal moves from the selected pawn
        validMoves: {
          e2: ["d3", "f3", "e3", "e4"], // Diagonal moves + normal moves
          d2: ["c3", "e3", "d3", "d4"], // Other pawns can also move
          f2: ["e3", "g3", "f3", "f4"], // Multiple pawns with diagonal options
          g2: ["f3", "h3", "g3", "g4"], // Show many-to-many relationship
        },
        animation: {
          type: "arrow",
          duration: 2000,
          color: "#10b981", // emerald
          direction: "diagonal",
        },
        validation: {
          powerTriggered: "mammals-diagonal-pawn-move",
        },
        errorHandling: {
          wrongSquareMessage:
            "Try clicking on one of the highlighted diagonal squares",
          hintMessage:
            "Pawns can only leap diagonally forward to empty squares",
        },
      },

      {
        id: "final-completion",
        type: "observe",
        title: "Tutorial Complete!",
        description:
          "You've successfully learned the Mammal diagonal leap power and seen how the AI responds!",
        tooltip:
          "Great job! You can now use this power strategically in real games.",
        animation: {
          type: "glow",
          duration: 3000,
          color: "#10b981", // emerald-500 for success
        },
        autoAdvance: {
          enabled: true,
          delay: 2000,
        },
      },

      {
        id: "completion",
        type: "observe",
        title: "Mammal Power Mastered!",
        description:
          "You've learned the diagonal leap! Use it to outmaneuver opponents and create new attack angles.",
        tooltip:
          "Remember: 2 uses per game, pawns only, diagonal forward to empty squares",
        animation: {
          type: "glow",
          duration: 200,
          color: "#10b981", // emerald-500 for success
        },
      },
    ],
  },
  monkeys: {
    name: "Monkeys",
    description: "Agile primates with banana-powered knight jumps",
    keyPower: "Banana Toss",
    animationDescription:
      "Twice per game, your pawns can move like knights (L-shaped moves)",
    demonstrationMoves: [
      {
        from: "e2",
        to: "d4",
        piece: "P",
        description: "Pawn jumps like a knight in L-shape",
      },
      {
        from: "d4",
        to: "f5",
        piece: "P",
        description: "Another knight-like jump to surprise opponents",
      },
    ],
    constants: {
      MAX_USES: 2,
    },
    tutorialConfig: {
      autoStart: false,
      startOnBoardClick: true,
    },
    tutorialSteps: [
      {
        id: "intro",
        type: "observe",
        title: "Welcome to Monkey Power!",
        description:
          "Monkeys can toss bananas to make their pawns jump like knights! Let's learn how!",
        tooltip: "Watch as we demonstrate the banana toss mechanic",
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
      {
        id: "select-pawn",
        type: "click",
        title: "Select Your Pawn",
        description: "First, click on one of your pawns to select it",
        tooltip: "Click on this pawn to select it for a banana toss",
        targetSquare: "e2",
        highlightSquares: ["e2"],
        expectedPiece: "p",
        animation: {
          type: "pulse",
          duration: 1500,
          color: "#f59e0b", // amber for banana
        },
        errorHandling: {
          wrongSquareMessage: "Try clicking on the highlighted pawn (e2)",
          hintMessage:
            "You need to select a pawn first before tossing a banana",
        },
      },
      {
        id: "knight-jump",
        type: "click",
        title: "Make Knight Jump",
        description:
          "Now click on a square where a knight could move from your pawn's position",
        tooltip:
          "Click on this square. The banana will help your pawn jump like a knight!",
        targetSquare: "d4",
        highlightSquares: ["d4", "f4", "c3", "g3"],
        // Custom FEN with pawn on e2 ready for knight jump demonstration
        fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
        // Allow knight moves from the selected pawn
        validMoves: {
          e2: ["d4", "f4", "c3", "g3", "e3", "e4"], // Knight moves + normal moves
          d2: ["b3", "f3", "b1", "f1", "c4", "e4", "d3", "d4"], // Other pawns with knight options
          f2: ["d3", "h3", "d1", "h1", "e4", "g4", "f3", "f4"], // Multiple pawns
          g2: ["e3", "f4", "h4", "e1", "g3", "g4"], // Show variety
        },
        animation: {
          type: "bounce",
          duration: 2000,
          color: "#f59e0b", // amber for banana
        },
        validation: {
          powerTriggered: "monkeys-banana-toss",
        },
        errorHandling: {
          wrongSquareMessage:
            "Try clicking on one of the highlighted knight-move squares",
          hintMessage:
            "Pawns can jump like knights with banana power - L-shaped moves only",
        },
      },
      {
        id: "ai-response",
        type: "aiPlay",
        title: "AI Response",
        description:
          "Watch as the AI makes a move in response to your banana toss!",
        tooltip: "The AI will now make a random move to continue the game.",
        aiPlay: {
          delay: 1500,
          firstMove: false,
        },
      },
      {
        id: "completion",
        type: "observe",
        title: "Banana Toss Mastered!",
        description:
          "You've learned the banana toss! Use it to surprise opponents with unexpected pawn jumps.",
        tooltip: "Remember: 2 uses per game, pawns only, L-shaped knight moves",
        // Final state - show all possible knight moves
        validMoves: {
          d4: ["b3", "b5", "c2", "c6", "e2", "e6", "f3", "f5"],
          d2: ["b3", "f3", "b1", "f1", "c4", "e4"],
          f2: ["d3", "h3", "d1", "h1", "e4", "g4"],
          g2: ["e3", "f4", "h4", "e1"],
          h2: ["f3", "g4", "f1"],
          a2: ["b4", "c3", "c1"],
          b2: ["a4", "c4", "d3", "d1"],
          c2: ["a3", "b4", "d4", "e3", "e1"],
        },
        animation: {
          type: "glow",
          duration: 200,
          color: "#10b981", // emerald-500 for success
        },
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
    ],
  },
  felines: {
    name: "Felines",
    description: "Agile cats with powerful pouncing abilities",
    keyPower: "Pounce",
    animationDescription:
      "Twice per game, any piece can jump over an adjacent piece to land exactly 2 squares away",
    demonstrationMoves: [
      {
        from: "e4",
        to: "e6",
        piece: "P",
        description: "Pawn pounces over adjacent piece vertically",
      },
      {
        from: "d4",
        to: "f6",
        piece: "N",
        description: "Knight pounces diagonally over adjacent piece",
      },
    ],
    constants: {
      MAX_USES: 2,
    },
    tutorialConfig: {
      autoStart: false,
      startOnBoardClick: true,
    },
    tutorialSteps: [
      {
        id: "intro",
        type: "observe",
        title: "Welcome to Feline Power!",
        description:
          "Felines can pounce over adjacent pieces to land 2 squares away! Let's learn how!",
        tooltip: "Watch as we demonstrate the pouncing mechanic",
        fen: "rnbqkbnr/pppppppp/8/8/8/3PB3/PPP1PPPP/RN1QKBNR w KQkq - 0 1",
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
      {
        id: "select-piece",
        type: "click",
        title: "Select Any Piece",
        description: "First, click on any of your pieces to select it",
        tooltip: "Click on this piece to select it for a pounce",
        targetSquare: "e2",
        highlightSquares: ["e2"],
        expectedPiece: "p",
        animation: {
          type: "pulse",
          duration: 1500,
          color: "#8b5cf6", // purple for feline
        },
        errorHandling: {
          wrongSquareMessage: "Try clicking on the highlighted piece (e2)",
          hintMessage: "You need to select a piece first before pouncing",
        },
      },

      {
        id: "pounce-move",
        type: "click",
        title: "Make Pounce",
        description:
          "Now click on a square 2 spaces away, jumping over the adjacent piece",
        tooltip: "Click here to pounce! You'll jump over the piece in between.",
        targetSquare: "e4",
        highlightSquares: ["e4"],
        // Allow pounce moves - jumping over adjacent pieces
        validMoves: {
          e2: ["e4", "c4", "g4"], // Vertical and diagonal pounces
          d2: ["d4", "b4", "f4"], // Other pieces with pounce options
          f2: ["f4", "d4", "h4"], // Multiple pounce directions
          g2: ["g4", "e4"], // Show variety
        },
        animation: {
          type: "bounce",
          duration: 2000,
          color: "#8b5cf6", // purple for feline
        },
        validation: {
          powerTriggered: "felines-pounce",
        },
        errorHandling: {
          wrongSquareMessage:
            "Try clicking on the highlighted square to pounce over the piece",
          hintMessage:
            "Pounce moves jump exactly 2 squares over an adjacent piece",
        },
      },

      {
        id: "completion",
        type: "observe",
        title: "Pounce Mastered!",
        description:
          "You've learned the feline pounce! Use it to jump over obstacles and surprise opponents.",
        tooltip:
          "Remember: 2 uses per game, any piece, jump over adjacent pieces to land 2 squares away",
        animation: {
          type: "glow",
          duration: 200,
          color: "#10b981", // emerald-500 for success
        },
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
    ],
  },
  structures: {
    name: "Structures",
    description: "Emergency tunnel networks for desperate situations",
    keyPower: "Tunnel Network",
    animationDescription:
      "When your queen is lost, your king can teleport to any empty square of the same color (3+ squares away)",
    demonstrationMoves: [
      {
        from: "e1",
        to: "a5",
        piece: "K",
        description: "King teleports through tunnel to distant light square",
      },
      {
        from: "a5",
        to: "h8",
        piece: "K",
        description: "Another tunnel escape to safety",
      },
    ],
    constants: {
      MAX_USES: 2,
      MIN_DISTANCE: 3,
    },
    tutorialConfig: {
      autoStart: false,
      startOnBoardClick: true,
    },
    tutorialSteps: [
      {
        id: "intro",
        type: "observe",
        title: "Welcome to Structure Power!",
        description:
          "Structures provide emergency tunnel networks for your king when the queen is lost. Let's learn how!",
        tooltip: "Watch as we demonstrate the tunnel network mechanic",
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
      {
        id: "setup-emergency",
        type: "observe",
        title: "Emergency Situation",
        description:
          "First, we need to simulate losing your queen to activate the tunnel network",
        tooltip: "The tunnel network only works when your queen is captured",
        // Custom FEN with queen removed to simulate emergency situation
        fen: "r1b1k2r/1ppp1n1p/2n2qpb/p2Kp1p1/4P3/5N2/P6P/R2R4 w kq - 0 1",
        autoAdvance: {
          enabled: true,
          delay: 1500,
        },
      },
      {
        id: "select-king",
        type: "click",
        title: "Select Your King",
        description:
          "Now that your queen is lost, click on your king to activate the tunnel network",
        tooltip: "Click on your king to see available tunnel destinations",
        targetSquare: "d5",
        highlightSquares: ["d5"],
        expectedPiece: "k",
        animation: {
          type: "pulse",
          duration: 1500,
          color: "#6366f1", // indigo for structures
        },
        errorHandling: {
          wrongSquareMessage: "Try clicking on your king (d5)",
          hintMessage:
            "Only the king can use tunnel networks when the queen is lost",
        },
      },
      {
        id: "tunnel-escape",
        type: "click",
        title: "Tunnel to Safety",
        description:
          "Click on a distant empty square of the same color to tunnel there",
        tooltip:
          "Click here to tunnel! Must be same color square and 3+ squares away.",
        highlightSquares: [
          "f1",
          "a4",
          "a6",
          "c2",
          "e2",
          "g2",
          "g4",
          "g8",
          "h1",
          "h3",
          "h5",
        ],
        // Allow tunnel moves - same color squares 3+ distance away
        validMoves: {
          d5: [
            "f1",
            "a4",
            "a6",
            "c2",
            "e2",
            "g2",
            "g4",
            "g8",
            "h1",
            "h3",
            "h5",
          ],
        },
        animation: {
          type: "flash",
          duration: 2000,
          color: "#6366f1", // indigo for structures
        },
        validation: {
          powerTriggered: "structures-tunnel-network",
        },
        errorHandling: {
          wrongSquareMessage:
            "Try clicking on one of the highlighted tunnel destinations",
          hintMessage:
            "Tunnels only work to empty squares of the same color, 3+ squares away",
        },
      },
      {
        id: "completion",
        type: "observe",
        title: "Tunnel Network Mastered!",
        description:
          "You've learned the emergency tunnel network! Use it to escape danger when your queen is lost.",
        tooltip:
          "Remember: King only, when queen is lost, same color squares, 3+ distance away",
        animation: {
          type: "glow",
          duration: 200,
          color: "#10b981", // emerald-500 for success
        },
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
    ],
  },
  plants: {
    name: "Plants",
    description: "Growing gardens with seed propagation abilities",
    keyPower: "Seed Propagation",
    animationDescription:
      "Once per game, your knights can plant seeds (pawns) on adjacent empty squares. Seeds grow into knights after 3 turns",
    demonstrationMoves: [
      {
        from: "g1",
        to: "f3",
        piece: "N",
        description: "Knight moves to position for seed planting",
      },
      {
        from: "f3",
        to: "e3",
        piece: "P",
        description: "Knight plants seed on adjacent empty square",
      },
      {
        from: "e3",
        to: "e3",
        piece: "N",
        description: "Seed matures into knight after 3 turns",
      },
    ],
    constants: {
      MAX_USES: 1,
      GROWTH_TURNS: 3,
    },
    tutorialConfig: {
      autoStart: false,
      startOnBoardClick: true,
    },
    tutorialSteps: [
      {
        id: "intro",
        type: "observe",
        title: "Welcome to Plant Power!",
        description:
          "Plants can propagate by planting seeds that grow into knights. Let's learn how!",
        tooltip: "Watch as we demonstrate the seed propagation mechanic",
        fen: "4k3/8/8/8/8/5NP1/8/4K3 w - - 0 1",
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
      {
        id: "select-knight",
        type: "click",
        title: "Select Your Knight",
        description: "First, click on one of your knights to select it",
        tooltip: "Click on this knight to select it for seed planting",
        targetSquare: "f3",
        highlightSquares: ["f3"],
        expectedPiece: "n",
        animation: {
          type: "pulse",
          duration: 1500,
          color: "#22c55e", // green for plants
        },
        errorHandling: {
          wrongSquareMessage: "Try clicking on the highlighted knight (g1)",
          hintMessage:
            "You need to select a knight first before planting seeds",
        },
      },
      {
        id: "plant-seed",
        type: "click",
        title: "Plant a Seed",
        description: "Now click on an adjacent empty square to plant a seed",
        tooltip:
          "Click here to plant a seed! Knights can only plant on adjacent squares.",
        // Allow adjacent moves from the selected knight

        animation: {
          type: "glow",
          duration: 4000,
          color: "#22c55e", // green for plants
        },
        validation: {
          powerTriggered: "plants-knight-propagation",
        },
        errorHandling: {
          wrongSquareMessage:
            "Try clicking on one of the highlighted adjacent squares",
          hintMessage:
            "Knights can only plant seeds on adjacent empty squares (one square away)",
        },
      },
      {
        id: "seed-explanation",
        type: "observe",
        title: "Seed Planted!",
        description:
          "Great! You've planted a seed. It will grow into a knight after 3 turns.",
        tooltip:
          "Seeds (🌰) automatically grow stronger each turn and become knights (🥀) after 3 turns",
        highlightSquares: ["f3"],
        animation: {
          type: "pulse",
          duration: 2000,
          color: "#16a34a", // darker green
        },
        autoAdvance: {
          enabled: true,
          delay: 2000,
        },
      },
      {
        id: "ai-response",
        type: "aiPlay",
        title: "AI Response",
        description:
          "Watch as the AI makes a move in response to your seed planting!",
        aiPlay: {
          delay: 200,
          firstMove: false,
        },
      },
      {
        id: "player-move",
        type: "playerMove",
        title: "Player Move",
        fromSquare: "e1",
        toSquare: "d1",
        description: "After your move, the counter will decrease by 1",
        tooltip: "The counter is going to decrease by 1. Only 2 moves left!",
        delay: 2000,
      },
      {
        id: "ai-response-2",
        type: "aiPlay",
        title: "AI Response",
        description:
          "Watch as the AI makes a move in response to your seed planting!",
        tooltip: "The AI will now make a random move to continue the game.",
        aiPlay: {
          delay: 200,
          firstMove: false,
        },
      },
      {
        id: "player-move-2",
        type: "playerMove",
        title: "Player Move",
        fromSquare: "d1",
        toSquare: "e1",
        description: "After your move, the counter will decrease by 1",
        tooltip: "The counter is going to decrease by 1. Only 1 move left!",
        delay: 2000,
      },
      {
        id: "ai-response-3",
        type: "aiPlay",
        title: "AI Response",
        description:
          "Watch as the AI makes a move in response to your seed planting!",
        tooltip: "The AI will now make a random move to continue the game.",
        aiPlay: {
          delay: 200,
          firstMove: false,
        },
      },
      {
        id: "player-move-3",
        type: "playerMove",
        title: "Player Move",
        fromSquare: "e1",
        toSquare: "f1",
        description: "After this move, your seedling will grow into a knight!",
        tooltip: "After this move, your seedling will grow into a knight!",
        delay: 2000,
      },

      {
        id: "completion",
        type: "observe",
        title: "Seed Propagation Mastered!",
        description:
          "You've learned seed propagation! Use it to create powerful knight networks across the board.",
        tooltip:
          "Remember: 1 use per game, knights only, adjacent squares, seeds grow automatically",
        animation: {
          type: "glow",
          duration: 200,
          color: "#10b981", // emerald-500 for success
        },
        autoAdvance: {
          enabled: true,
          delay: 100,
        },
      },
    ],
  },
};

